import React, { useState, useEffect, useCallback } from 'react';
import { getBuilds, updateBuildEncryption, updateAppStoreVersion } from '../../services/api';

function TestFlightSection({ appId, currentVersion, onBuildReady }) {
  const [loading, setLoading] = useState(false);
  const [builds, setBuilds] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [encryptionChoice, setEncryptionChoice] = useState('NO');
  const [linkingBuild, setLinkingBuild] = useState(false);

  const loadBuilds = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBuilds(appId);
      console.log('Builds response:', response.data);
      if (response.data.success) {
        setBuilds(response.data.builds || []);
      }
    } catch (err) {
      console.error('Failed to load builds:', err);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    if (appId) {
      loadBuilds();
    }
  }, [appId, loadBuilds]);

  const getBuildStatus = (build) => {
    const processingState = build.attributes?.processingState;
    const usesEncryption = build.attributes?.usesNonExemptEncryption;

    if (processingState !== 'VALID') {
      return { status: 'Processing', color: '#ff9500', action: null };
    }

    // If encryption is not set (undefined/null), it needs compliance
    if (usesEncryption === undefined || usesEncryption === null) {
      return { status: 'Missing Compliance', color: '#ff3b30', action: 'SET_COMPLIANCE' };
    }

    return { status: 'Ready to Submit', color: '#34c759', action: null };
  };

  const isVersionEditable = () => {
    if (!currentVersion) return false;
    const state = currentVersion.attributes?.appStoreState;
    return state === 'PREPARE_FOR_SUBMISSION' || state === 'REJECTED' || state === 'READY_FOR_REVIEW' || state === 'DEVELOPER_REJECTED';
  };

  const getLinkedBuild = () => {
    if (!currentVersion?.relationships?.build?.data) return null;
    return builds.find(build => build.id === currentVersion.relationships.build.data.id);
  };

  const handleLinkBuild = async (build) => {
    if (!currentVersion || !isVersionEditable()) return;

    setLinkingBuild(true);
    try {
      const response = await updateAppStoreVersion(currentVersion.id, { buildId: build.id });
      if (response.data.success) {
        alert('Build linked successfully!');
        if (onBuildReady) {
          onBuildReady(response.data.version); // Pass the updated version with build relationship
        }
      } else {
        alert('Failed to link build: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to link build'
        : err.response?.data?.error || err.message || 'Failed to link build';
      alert('Failed to link build: ' + errorMsg);
    } finally {
      setLinkingBuild(false);
    }
  };

  const handleUnlinkBuild = async () => {
    if (!currentVersion || !isVersionEditable()) return;

    setLinkingBuild(true);
    try {
      const response = await updateAppStoreVersion(currentVersion.id, { buildId: null });
      if (response.data.success) {
        alert('Build unlinked successfully!');
        if (onBuildReady) {
          onBuildReady(response.data.version); // Pass the updated version with build relationship
        }
      } else {
        alert('Failed to unlink build: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to unlink build'
        : err.response?.data?.error || err.message || 'Failed to unlink build';
      alert('Failed to unlink build: ' + errorMsg);
    } finally {
      setLinkingBuild(false);
    }
  };

  const handleSetCompliance = (build) => {
    setSelectedBuild(build);
    setShowComplianceModal(true);
  };

  const submitCompliance = async () => {
    if (!selectedBuild) return;

    try {
      const usesNonExemptEncryption = encryptionChoice === 'YES';
      await updateBuildEncryption(selectedBuild.id, usesNonExemptEncryption);
      
      alert('Compliance information submitted successfully!');
      setShowComplianceModal(false);
      setSelectedBuild(null);
      setEncryptionChoice('NO');
      
      // Reload builds to get updated status
      await loadBuilds();
      
      // Notify parent that a build is ready
      if (onBuildReady) {
        onBuildReady(selectedBuild);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to submit compliance'
        : err.response?.data?.error || err.message || 'Failed to submit compliance';
      alert('Failed to submit compliance: ' + errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-secondary" style={{ marginBottom: '20px' }}>
        Select a build and ensure export compliance is set before submitting to the App Store.
      </p>

      {currentVersion && (
        <div className="info-card">
          <h4 className="info-card-title">
            Version {currentVersion.attributes.versionString}
          </h4>
          <p className="info-card-text">
            <strong>State:</strong> {currentVersion.attributes.appStoreState}
          </p>
          <p className="info-card-text">
            <strong>Linked Build:</strong> {getLinkedBuild() ? 
              `${getLinkedBuild().attributes.version} (${getLinkedBuild().id.substring(0, 8)}...)` : 
              'None'}
          </p>
          {isVersionEditable() && (
            <p className="info-card-hint">
              ✓ Version is editable - you can link/unlink builds
              {currentVersion.attributes.appStoreState === 'DEVELOPER_REJECTED' && 
                ' (Developer rejected versions can be modified and resubmitted)'}
            </p>
          )}
          {!isVersionEditable() && (
            <p className="info-card-hint muted">
              ⓘ Version is not editable in current state. Build linking is only available for versions in 
              "Prepare for Submission", "Ready for Review", or "Developer Rejected" states.
            </p>
          )}
        </div>
      )}

      {builds.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <p className="empty-state-title">No builds available</p>
          <p className="empty-state-text">Upload a build from Xcode or your CI/CD pipeline</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Build Number</th>
                <th>Upload Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {builds.map(build => {
                const buildInfo = getBuildStatus(build);
                const uploadDate = build.attributes?.uploadedDate 
                  ? new Date(build.attributes.uploadedDate).toLocaleDateString()
                  : 'N/A';

                return (
                  <tr key={build.id}>
                    <td>{build.attributes?.version || 'N/A'}</td>
                    <td className="mono">{build.id.substring(0, 8)}...</td>
                    <td>{uploadDate}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={`badge ${
                          buildInfo.status === 'Ready to Submit' && getLinkedBuild()?.id === build.id ? 'badge-success' 
                          : buildInfo.status === 'Ready to Submit' ? 'badge-success' 
                          : buildInfo.status === 'Missing Compliance' ? 'badge-error' 
                          : 'badge-warning'
                        }`}>
                          {buildInfo.status === 'Ready to Submit' && getLinkedBuild()?.id === build.id ? 'Linked' : buildInfo.status}
                        </span>
                        {buildInfo.status === 'Ready to Submit' && getLinkedBuild()?.id === build.id && isVersionEditable() && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={handleUnlinkBuild}
                            disabled={linkingBuild}
                            title="Unlink this build from the current version"
                          >
                            {linkingBuild ? '...' : '×'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      {buildInfo.action === 'SET_COMPLIANCE' && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSetCompliance(build)}
                        >
                          Set Compliance
                        </button>
                      )}
                      {buildInfo.status === 'Ready to Submit' && isVersionEditable() && getLinkedBuild()?.id !== build.id && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleLinkBuild(build)}
                          disabled={linkingBuild}
                        >
                          {linkingBuild ? 'Linking...' : 'Link Build'}
                        </button>
                      )}
                      {buildInfo.status === 'Ready to Submit' && !isVersionEditable() && (
                        <span className="text-success">✓ Ready</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Compliance Modal */}
      {showComplianceModal && (
        <div className="modal-overlay" onClick={() => setShowComplianceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Export Compliance</h2>
              <button className="modal-close" onClick={() => setShowComplianceModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '20px' }}>
                <strong className="text-primary">Does your app use encryption?</strong>
              </p>

              <p className="text-secondary" style={{ marginBottom: '20px', fontSize: '14px' }}>
                Apps that use certain encryption algorithms require export documentation from the U.S. government. 
                Most apps don't use these types of encryption.
              </p>

              <div className="form-group">
                <label className="form-label">Export Compliance Status</label>
                
                <div style={{ marginTop: '16px' }}>
                  <label className={`radio-option ${encryptionChoice === 'NO' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="encryption"
                      value="NO"
                      checked={encryptionChoice === 'NO'}
                      onChange={(e) => setEncryptionChoice(e.target.value)}
                    />
                    <strong>No, my app doesn't use encryption</strong>
                    <p>
                      Your app doesn't use any of the encryption algorithms mentioned, or only uses encryption that's exempt (like HTTPS).
                    </p>
                  </label>

                  <label className={`radio-option ${encryptionChoice === 'YES' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="encryption"
                      value="YES"
                      checked={encryptionChoice === 'YES'}
                      onChange={(e) => setEncryptionChoice(e.target.value)}
                    />
                    <strong>Yes, my app uses non-exempt encryption</strong>
                    <p>
                      Your app implements custom encryption algorithms or uses encryption for purposes other than within HTTPS.
                    </p>
                  </label>
                </div>
              </div>

              <div className="alert alert-warning">
                <span className="alert-icon">ℹ️</span>
                <span><strong>Most apps select "No"</strong> because standard HTTPS connections and other system-provided encryption are exempt from export requirements.</span>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-ghost" 
                onClick={() => setShowComplianceModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={submitCompliance}
              >
                Submit Compliance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestFlightSection;
